<template>
    <b-modal
        v-model="isVisible"
        :title="mode === 'create' ? '조직 추가' : '조직 편집'"
        size="xl"
        centered
        @ok="handleOk"
        @cancel="handleCancel"
        @hidden="handleHidden"
        ok-title="저장"
        cancel-title="취소"
        modal-class="boundary-modal-high-z"
    >
        <div class="org-modal-content">
            <!-- 핵심 정보 -->
            <section class="form-section">
                <h4 class="section-title">조직 정보</h4>
                <div class="form-grid">
                    <div class="form-field">
                        <label class="form-label"> 조직명 </label>
                        <input
                            type="text"
                            :value="currentOrgData.orgName || currentOrgData.name"
                            @input="updateOrgField('orgName', $event.target.value)"
                            class="form-control"
                            placeholder="조직명을 입력하세요"
                        />
                    </div>
                    <div class="form-field">
                        <label class="form-label"> 조직코드 </label>
                        <input
                            type="text"
                            :value="currentOrgData.orgCode"
                            @input="updateOrgField('orgCode', $event.target.value)"
                            class="form-control"
                            placeholder="자동 생성"
                        />
                    </div>
                    <div class="form-field">
                        <label class="form-label"> 사업자번호 </label>
                        <input
                            type="text"
                            :value="currentOrgData.businessNumber"
                            @input="updateOrgField('businessNumber', $event.target.value)"
                            class="form-control"
                            placeholder="123-45-67890"
                        />
                    </div>
                    <div class="form-field">
                        <label class="form-label"> 산업 </label>
                        <input
                            type="text"
                            :value="currentOrgData.industry"
                            @input="updateOrgField('industry', $event.target.value)"
                            class="form-control"
                            placeholder="제조업"
                        />
                    </div>
                    <div class="form-field full-span">
                        <label class="form-label"> 주소 </label>
                        <input
                            type="text"
                            :value="currentOrgData.address"
                            @input="updateOrgField('address', $event.target.value)"
                            class="form-control"
                            placeholder="서울시 강남구 테헤란로 123"
                        />
                    </div>
                </div>
            </section>

            <!-- 연락처 정보 -->
            <section class="form-section">
                <h4 class="section-title">연락처 정보</h4>
                <div class="form-grid">
                    <div class="form-field">
                        <label class="form-label"> 담당자 </label>
                        <input
                            type="text"
                            :value="currentOrgData.contactPerson"
                            @input="updateOrgField('contactPerson', $event.target.value)"
                            class="form-control"
                            placeholder="홍길동"
                        />
                    </div>
                    <div class="form-field">
                        <label class="form-label"> 이메일 </label>
                        <input
                            type="email"
                            :value="currentOrgData.contactEmail"
                            @input="updateOrgField('contactEmail', $event.target.value)"
                            class="form-control"
                            placeholder="contact@example.com"
                        />
                    </div>
                    <div class="form-field">
                        <label class="form-label"> 연락처 </label>
                        <input
                            type="tel"
                            :value="currentOrgData.contactPhone"
                            @input="updateOrgField('contactPhone', $event.target.value)"
                            class="form-control"
                            placeholder="02-1234-5678"
                        />
                    </div>
                </div>
            </section>

            <!-- 회계 정보 -->
            <section class="form-section">
                <h4 class="section-title">회계 정보</h4>
                <div class="form-grid">
                    <div class="form-field">
                        <label class="form-label"> 회계연도 종료일 </label>
                        <input
                            type="text"
                            :value="currentOrgData.fiscalYearEnd"
                            @input="updateOrgField('fiscalYearEnd', $event.target.value)"
                            class="form-control"
                            placeholder="1231"
                            maxlength="4"
                        />
                    </div>
                    <div class="form-field">
                        <label class="form-label"> 탄소 회계 방법 </label>
                        <b-form-select
                            :value="currentOrgData.accountingMethod || currentOrgData.carbonAccountingMethod"
                            @change="updateOrgField('accountingMethod', $event)"
                            :options="accountingMethodOptions"
                            class="form-control"
                        ></b-form-select>
                    </div>
                </div>
            </section>

            <!-- 비고 -->
            <section class="form-section">
                <h4 class="section-title">비고</h4>
                <div class="form-field">
                    <label class="form-label"> 추가 정보 </label>
                    <textarea
                        :value="currentOrgData.remarks"
                        @input="updateOrgField('remarks', $event.target.value)"
                        class="form-control"
                        placeholder="비고사항을 입력하세요"
                        rows="3"
                    ></textarea>
                </div>
            </section>
        </div>
    </b-modal>
</template>

<script>
import carbonApi from "../../../api";
import { unwrapSuccess, extractErrorMessage } from "../../../utils/api.js";
import CarbonCodes from "../../../codes";

export default {
    name: "BoundaryOrgModal",
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: "create",
            validator: (value) => ["create", "edit"].includes(value),
        },
        orgData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            saving: false,
            internalFormData: null, // 내부 formData 관리용
        };
    },
    computed: {
        isVisible: {
            get() {
                return this.show;
            },
            set(value) {
                this.$emit("update:show", value);
            },
        },
        // 내부 formData가 있으면 사용, 없으면 orgData prop 사용
        currentOrgData() {
            if (this.internalFormData) {
                return this.internalFormData;
            }
            return this.orgData;
        },
        accountingMethodOptions() {
            return [{ value: "", text: "선택하세요" }, ...CarbonCodes.accountingMethod.getList()];
        },
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.loadFormData();
            }
        },
        orgData: {
            handler() {
                if (this.show) {
                    this.loadFormData();
                }
            },
            deep: true,
        },
    },
    mounted() {
        if (this.show) {
            this.loadFormData();
        }
    },
    methods: {
        loadFormData() {
            if (!this.orgData) {
                this.internalFormData = null;
                return;
            }

            // orgData를 내부 formData로 복사
            this.internalFormData = {
                orgName: this.orgData.orgName || this.orgData.name || "",
                orgCode: this.orgData.orgCode || "",
                businessNumber: this.orgData.businessNumber || "",
                industry: this.orgData.industry || "",
                address: this.orgData.address || "",
                contactPerson: this.orgData.contactPerson || "",
                contactEmail: this.orgData.contactEmail || "",
                contactPhone: this.orgData.contactPhone || "",
                fiscalYearEnd: this.orgData.fiscalYearEnd || "1231",
                accountingMethod:
                    this.orgData.accountingMethod || this.orgData.carbonAccountingMethod || "FINANCIAL_CONTROL",
                remarks: this.orgData.remarks || "",
                orgIdx: this.orgData.orgIdx || null,
            };
        },
        updateOrgField(field, value) {
            // 내부 formData가 있으면 내부에서 관리
            if (this.internalFormData) {
                this.$set(this.internalFormData, field, value);
            } else {
                // 부모 컴포넌트에서 관리하는 경우
                this.$emit("update-org-field", field, value);
            }
        },
        async handleOk(bvModalEvt) {
            // 모달 자동 닫힘 방지
            bvModalEvt.preventDefault();

            // Validation
            const orgName = this.currentOrgData.orgName || this.currentOrgData.name;
            if (!orgName || !orgName.trim()) {
                this.$bvToast.toast("조직명을 입력해주세요.", {
                    variant: "warning",
                    title: "입력 필요",
                });
                return;
            }

            if (this.mode === "edit") {
                const orgCode = this.currentOrgData.orgCode;
                if (!orgCode || !orgCode.trim()) {
                    this.$bvToast.toast("조직 코드를 입력해주세요.", {
                        variant: "warning",
                        title: "입력 필요",
                    });
                    return;
                }
            }

            this.saving = true;
            try {
                let responseData;
                if (this.mode === "create") {
                    responseData = await this.saveCreateFlow();
                } else {
                    responseData = await this.saveEditFlow();
                }

                // saved 이벤트에 orgIdx와 orgData 포함
                const orgIdx = this.currentOrgData.orgIdx || responseData?.orgIdx;
                this.$emit("saved", { orgIdx, orgData: responseData || this.currentOrgData });
                this.$emit("update:show", false);
            } catch (error) {
                console.error("[BoundaryOrgModal] 저장 중 오류:", error);
                const msg = extractErrorMessage(error, "저장 중 오류가 발생했습니다.");
                this.$bvToast.toast(msg, {
                    variant: "danger",
                    title: "저장 실패",
                });
            } finally {
                this.saving = false;
            }
        },
        handleCancel() {
            this.$emit("cancel");
        },
        handleHidden() {
            this.$emit("update:show", false);
        },
        async saveCreateFlow() {
            const orgPayload = {
                orgName: (this.currentOrgData.orgName || this.currentOrgData.name || "").trim(),
                orgCode: (this.currentOrgData.orgCode || "").trim(),
                businessNumber: (this.currentOrgData.businessNumber || "").trim(),
                industry: (this.currentOrgData.industry || "").trim(),
                address: (this.currentOrgData.address || "").trim(),
                contactPerson: (this.currentOrgData.contactPerson || "").trim(),
                contactEmail: (this.currentOrgData.contactEmail || "").trim(),
                contactPhone: (this.currentOrgData.contactPhone || "").trim(),
                fiscalYearEnd: this.currentOrgData.fiscalYearEnd || "1231",
                accountingMethod:
                    this.currentOrgData.accountingMethod ||
                    this.currentOrgData.carbonAccountingMethod ||
                    "FINANCIAL_CONTROL",
                remarks: (this.currentOrgData.remarks || "").trim(),
            };
            const response = await carbonApi.organization.createOrg(orgPayload);
            return unwrapSuccess(response, "조직 생성에 실패했습니다.");
        },
        async saveEditFlow() {
            const orgIdx = this.currentOrgData.orgIdx;
            if (!orgIdx) {
                throw new Error("유효한 orgIdx가 없습니다.");
            }

            const orgPayload = {
                orgName: (this.currentOrgData.orgName || this.currentOrgData.name || "").trim(),
                orgCode: (this.currentOrgData.orgCode || "").trim(),
                businessNumber: (this.currentOrgData.businessNumber || "").trim(),
                industry: (this.currentOrgData.industry || "").trim(),
                address: (this.currentOrgData.address || "").trim(),
                contactPerson: (this.currentOrgData.contactPerson || "").trim(),
                contactEmail: (this.currentOrgData.contactEmail || "").trim(),
                contactPhone: (this.currentOrgData.contactPhone || "").trim(),
                fiscalYearEnd: this.currentOrgData.fiscalYearEnd || "1231",
                carbonAccountingMethod:
                    this.currentOrgData.accountingMethod ||
                    this.currentOrgData.carbonAccountingMethod ||
                    "FINANCIAL_CONTROL",
                remarks: (this.currentOrgData.remarks || "").trim(),
            };

            const response = await carbonApi.organization.updateOrg(orgIdx, orgPayload);
            return unwrapSuccess(response, "조직 수정에 실패했습니다.");
        },
    },
};
</script>

<style scoped>
.org-modal-content {
    max-height: 75vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding: 8px;
}

/* 섹션 스타일 - 중요도별 */
.form-section {
    background: #ffffff;
    padding: 0;
    transition: all 0.2s ease;
}

.section-title {
    font-size: 15px;
    font-weight: 700;
    color: #1e293b;
    letter-spacing: -0.01em;
    margin-bottom: 12px;
}

.form-section {
    padding-bottom: 0;
    margin-bottom: 32px;
}

.form-section:last-child {
    margin-bottom: 0;
}

/* 그리드 레이아웃 - 2열 */
.form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0 20px;
}

.form-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 4px 0;
}

.form-field.full-span {
    grid-column: 1 / -1;
}
.form-grid label {
    margin: 0px;
}

/* 라벨 스타일 */
.form-label {
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;
    color: #334155;
}

/* 입력 필드 스타일 */
.form-control {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    font-size: 13px;
    color: #0f172a;
    background: #ffffff;
    transition: all 0.2s ease;
    line-height: 1.5;
    font-family: inherit;
    height: 36px;
}

.form-control:focus {
    outline: none;
    border-color: #cbd5e1;
    background: #fafbfc;
}

.form-control::placeholder {
    color: #94a3b8;
    font-size: 12px;
    font-weight: 400;
}

textarea.form-control {
    resize: vertical;
    padding: 10px 12px;
    font-family: inherit;
    line-height: 1.6;
    height: auto;
    min-height: 80px;
}

/* 반응형 */
@media (max-width: 1200px) {
    .form-grid {
        grid-template-columns: 1fr;
        gap: 14px;
    }
}

@media (max-width: 768px) {
    .org-modal-content {
        gap: 24px;
        padding: 6px 2px 2px 0;
    }

    .form-field {
        padding: 14px 0;
    }

    .form-grid {
        grid-template-columns: 1fr;
    }
}
</style>
